/**
 * The compile context — everything a block may know beyond its own slots.
 *
 * Two properties are load-bearing:
 *
 *  - **Nothing here is a Regta resource.** A party is a literal, not a
 *    `party_ref` into Regta's database; a property is a literal, not a row
 *    id. An API consumer composing a document from its own data (or from
 *    nothing at all) passes plain objects and gets the same output a
 *    Regta-hosted contract would. `id` exists but is always optional, and
 *    the framework never dereferences it.
 *  - **`compile` is pure.** `now` is injected rather than read from the
 *    clock, formatters are fixed by locale, and a block cannot reach into
 *    a neighbouring block. Same input + same compiler version + same
 *    context ⇒ byte-identical IR, which is what makes snapshot testing and
 *    revision hashing meaningful.
 */
import { formatSwedishLegalId } from './legal-id.js';
function makeFormatters(locale) {
    const num = (digits) => new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    return {
        date: (value) => (value ? String(value).slice(0, 10) : null),
        number: (value, fractionDigits = 0) => (value == null ? null : num(fractionDigits).format(value)),
        currency: (value, unit = 'kr') => (value == null ? null : `${num(0).format(value)} ${unit}`),
        percent: (value) => value == null ? null : `${num(Number.isInteger(value) ? 0 : 2).format(value)} %`,
    };
}
/**
 * Builds a compile context from plain data. Every field is optional except
 * `now` — a caller with nothing but a clock gets a valid context that
 * compiles blank forms.
 */
export function createCompileContext(input) {
    const locale = input.locale ?? 'sv-SE';
    const parties = [...(input.parties ?? [])].map((p) => ({ ...p, ordinal: p.ordinal ?? 0 }));
    return {
        locale,
        now: input.now,
        mode: input.mode ?? 'draft',
        properties: input.properties ?? [],
        attachments: input.attachments ?? [],
        document: {
            title: input.document?.title ?? '',
            reference: input.document?.reference ?? null,
            issuedAt: input.document?.issuedAt ?? null,
        },
        format: makeFormatters(locale),
        custom: Object.freeze({ ...(input.custom ?? {}) }),
        parties: {
            all: () => parties,
            byRole: (role) => parties.filter((p) => p.role === role).sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0)),
            slot: (role, ordinal = 0) => parties.find((p) => p.role === role && (p.ordinal ?? 0) === ordinal),
        },
        refs: {
            section: (target, display = 'number') => ({ type: 'sectionRef', target, display }),
            attachment: (target, display = 'numberAndLabel') => ({ type: 'attachmentRef', target, display }),
        },
    };
}
/** Renders a party's postal address as one display line. */
export function formatAddress(address) {
    if (!address)
        return null;
    const line = [address.street, [address.postalCode, address.city].filter(Boolean).join(' ')]
        .filter((part) => part && String(part).trim() !== '')
        .join(', ');
    return line === '' ? null : line;
}
/** "Bostads AB (org.nr 559123-4567)" — the standard party display string. */
export function formatPartyName(party, opts = {}) {
    if (!party)
        return null;
    if (!opts.withLegalId || !party.legalId)
        return party.name;
    const label = party.kind === 'organisation' ? 'org.nr' : 'pers.nr';
    return `${party.name} (${label} ${formatSwedishLegalId(party.legalId)})`;
}
//# sourceMappingURL=context.js.map